/**
 * The marketer mirror's data hook (SKE-4166, phase 2).
 *
 * Binds React to the demo store — the same store the approver UI writes on
 * every approve / reject / note — via `useSyncExternalStore`. There is no
 * polling, no second fetch and no duplicated state: the store publishes, the
 * mirror re-renders, and both sides of the demo are guaranteed to agree because
 * they are reading one object.
 *
 * On top of that it owns the only genuinely stateful part of the mirror: the
 * short-lived "your client just did something" notices. The store's action
 * trail is append-only, so "what is new" is simply "everything past the entry
 * count we had last time we looked".
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import type { MirrorNotice, MirrorPost, MirrorProgress } from './demoMirror'
import {
  mirrorProgress,
  noticesSince,
  recentNotices,
  toMirrorPosts,
} from './demoMirror'
import { demoStrings } from './demoStrings'
import { getDemoStoreSnapshot, subscribeDemoStore } from './demoStore'

/** How long a toast stays up. Long enough to read a caption, short enough not to nag. */
export const MIRROR_NOTICE_TTL_MS = 4200

/** At most this many toasts stack before the oldest is dropped. */
const MAX_VISIBLE_NOTICES = 3

export interface DemoMirrorState {
  /** Every demo post as the marketer's calendar would list it. */
  posts: MirrorPost[]
  progress: MirrorProgress
  /** Newest-first history, including actions from before this page load. */
  activity: MirrorNotice[]
  /** Currently-visible toasts, oldest first. */
  notices: MirrorNotice[]
  /** The latest action, or null before the visitor has done anything here. */
  latestNotice: MirrorNotice | null
  /** Posts whose status changed recently enough to still be highlighted. */
  flashedPostIds: Set<string>
  /**
   * The agency name in the mirror's mock Sked chrome — the visitor's own client
   * once they have personalized the board (SKE-4166 phase 4), the demo's
   * fictional agency until then.
   */
  workspaceName: string
}

export const useDemoMirror = (): DemoMirrorState => {
  const store = useSyncExternalStore(subscribeDemoStore, getDemoStoreSnapshot)

  const posts = useMemo(() => toMirrorPosts(store), [store])
  const progress = useMemo(() => mirrorProgress(posts), [posts])
  const activity = useMemo(() => recentNotices(store, posts), [store, posts])

  const [notices, setNotices] = useState<MirrorNotice[]>([])
  const [latestNotice, setLatestNotice] = useState<MirrorNotice | null>(null)

  /**
   * How many trail entries we had already accounted for. `null` means "no
   * baseline yet" — the store is still loading, or was just reset.
   *
   * The baseline matters: a returning visitor's snapshot arrives with their
   * previous actions already in it, and replaying those as live toasts would
   * be a lie. Only what happens in front of us animates.
   */
  const seenCountRef = useRef<number | null>(null)

  useEffect(() => {
    const actions = store?.actions

    if (!actions) {
      // Store absent (first paint) or cleared (Reset demo). Drop the baseline
      // so the next snapshot re-establishes it instead of replaying history.
      seenCountRef.current = null
      setNotices((current) => (current.length > 0 ? [] : current))
      setLatestNotice(null)
      return
    }

    if (
      seenCountRef.current === null ||
      actions.length < seenCountRef.current
    ) {
      // First snapshot, or the trail shrank (a reset rebuilt it) — re-baseline
      // silently.
      seenCountRef.current = actions.length
      return
    }

    const fresh = noticesSince(store, seenCountRef.current, posts)
    seenCountRef.current = actions.length

    if (fresh.length === 0) {
      return
    }

    setNotices((current) => [...current, ...fresh].slice(-MAX_VISIBLE_NOTICES))
    setLatestNotice(fresh[fresh.length - 1])
  }, [store, posts])

  // Drain the queue oldest-first. A new arrival restarts the timer, which
  // extends the current oldest toast slightly — acceptable, and it keeps this
  // to a single timer rather than one per notice.
  useEffect(() => {
    if (notices.length === 0) {
      return
    }

    const timer = setTimeout(
      () => setNotices((current) => current.slice(1)),
      MIRROR_NOTICE_TTL_MS
    )

    return () => clearTimeout(timer)
  }, [notices])

  const flashedPostIds = useMemo(
    () => new Set(notices.map((notice) => notice.postId)),
    [notices]
  )

  // Read off the same store snapshot as everything else here, so personalizing
  // the board updates the mirror in the same render as the approver side.
  const workspaceName =
    store?.settings.demoBrand?.name || demoStrings.mirrorWorkspaceName

  return {
    posts,
    progress,
    activity,
    notices,
    latestNotice,
    flashedPostIds,
    workspaceName,
  }
}
